import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { X, Mail, Copy, Check, Code, ExternalLink, RefreshCw, Key, AlertTriangle, Puzzle, Download } from 'lucide-react';
import { closeModal, auth$ } from '../store';
import { apiConfig } from '../lib/apiConfig';
import './ExtensionModal.css';
import { Dialog, ICON_SIZE } from '../design-system';

// API URLs
const MCP_SERVER_URL = `${apiConfig.supabaseUrl}/functions/v1/reedle-mcp/mcp`;
const API_TOKENS_URL = `${apiConfig.supabaseUrl}/functions/v1/api-tokens`;

/**
 * Generate user's email save address from their account email
 * Rules:
 * - Remove @ and . from email
 * - Append @save-reedle.dimp.studio
 *
 * Example: dimpurr@live.com -> dimpurrlivecom@save-reedle.dimp.studio
 */
function generateSaveAddress(email) {
  if (!email) return null;
  // Remove @ and . from email, convert to lowercase
  const sanitized = email.toLowerCase().replace(/[@.]/g, '');
  return `${sanitized}@save-reedle.dimp.studio`;
}

const ExtensionModal = () => {
  const { t } = useTranslation('modals');
  const userEmail = useSelector(() => auth$.user.get()?.email);
  const session = useSelector(() => auth$.session.get());
  const saveAddress = generateSaveAddress(userEmail);
  const [copied, setCopied] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [configCopied, setConfigCopied] = useState(false);

  // Permanent API token state
  const [apiToken, setApiToken] = useState(null); // The actual token (only shown after generation)
  const [tokenInfo, setTokenInfo] = useState(null); // Token metadata from server
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [showNewToken, setShowNewToken] = useState(false);

  // Fetch token status on mount
  const fetchTokenStatus = useCallback(async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(API_TOKENS_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTokenInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch token status:', err);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchTokenStatus();
  }, [fetchTokenStatus]);

  // Generate new API token
  const handleGenerateToken = async () => {
    if (!session?.access_token) return;

    setTokenLoading(true);
    setTokenError(null);
    setApiToken(null);

    try {
      const response = await fetch(API_TOKENS_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate token');
      }

      const data = await response.json();
      setApiToken(data.token);
      setShowNewToken(true);
      setTokenInfo({ has_token: true, created_at: data.created_at });
    } catch (err) {
      console.error('Token generation error:', err);
      setTokenError('Failed to generate token. Please try again.');
    } finally {
      setTokenLoading(false);
    }
  };

  // Revoke API token
  const handleRevokeToken = async () => {
    if (!session?.access_token) return;
    if (!confirm('Are you sure you want to revoke your API token? Any clients using this token will stop working.')) {
      return;
    }

    setTokenLoading(true);
    setTokenError(null);

    try {
      const response = await fetch(API_TOKENS_URL, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to revoke token');
      }

      setApiToken(null);
      setShowNewToken(false);
      setTokenInfo({ has_token: false });
    } catch (err) {
      console.error('Token revocation error:', err);
      setTokenError('Failed to revoke token. Please try again.');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!saveAddress) return;
    try {
      await navigator.clipboard.writeText(saveAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyToken = async () => {
    if (!apiToken) return;
    try {
      await navigator.clipboard.writeText(apiToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy token:', err);
    }
  };

  const handleCopyConfig = async () => {
    const tokenToUse = apiToken || '<YOUR_API_TOKEN>';
    const config = JSON.stringify({
      mcpServers: {
        reedle: {
          type: 'http',
          url: MCP_SERVER_URL,
          headers: {
            Authorization: `Bearer ${tokenToUse}`
          }
        }
      }
    }, null, 2);
    try {
      await navigator.clipboard.writeText(config);
      setConfigCopied(true);
      setTimeout(() => setConfigCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy config:', err);
    }
  };

  // Truncate token for display
  const truncatedApiToken = apiToken
    ? `${apiToken.substring(0, 15)}...${apiToken.substring(apiToken.length - 8)}`
    : null;

  return (
    <Dialog
      isOpen
      onClose={closeModal}
      ariaLabel={t('extensionModal.title')}
      showClose={false}
      overlayClassName="extension-modal-overlay"
      className="extension-modal"
    >
      <button className="extension-modal-close" onClick={closeModal} aria-label={t('common.close', { defaultValue: 'Close' })}>
        <X size={ICON_SIZE.lg} />
      </button>

        <div className="extension-modal-header">
          <h2>{t('extensionModal.title')}</h2>
          <p className="extension-subtitle">{t('extensionModal.subtitle')}</p>
        </div>

        {/* Email Save Address Section */}
        <section className="extension-section email-save-section">
          <div className="email-save-header">
            <Mail size={ICON_SIZE.lg} />
            <h3>{t('extensionModal.email.title')}</h3>
          </div>
          <p>
            {t('extensionModal.email.description')}
          </p>
          {saveAddress ? (
            <div className="email-save-address-container">
              <code className="email-save-address">{saveAddress}</code>
              <button
                className="email-copy-btn"
                onClick={handleCopy}
                title={t('extensionModal.email.copyToClipboard')}
              >
                {copied ? <Check size={ICON_SIZE.sm} /> : <Copy size={ICON_SIZE.sm} />}
              </button>
            </div>
          ) : (
            <p className="email-save-note">{t('extensionModal.email.loginHint')}</p>
          )}
          <p className="email-save-tip">
            <strong>{t('extensionModal.email.tipLabel')}</strong> {t('extensionModal.email.tipText')}
          </p>
        </section>

        <div className="extension-divider" />

        {/* Browser Extension Section */}
        <section className="extension-section browser-extension-section">
          <div className="email-save-header">
            <Puzzle size={ICON_SIZE.lg} />
            <h3>{t('extensionModal.browserExtension.title')}</h3>
          </div>
          <p className="extension-subtitle-inline">{t('extensionModal.browserExtension.subtitle')}</p>

          <a
            href="https://chromewebstore.google.com/detail/reedle-save-to-read-later/bjcolallmgbdflfjohhgoiipakidnhlg"
            target="_blank"
            rel="noopener noreferrer"
            className="extension-download-btn"
          >
            <Download size={ICON_SIZE.md} />
            {t('extensionModal.browserExtension.addFromChromeStore')}
          </a>

          <div className="extension-subsection">
            <h4>{t('extensionModal.browserExtension.whyUse.title')}</h4>
            <p>
              {t('extensionModal.browserExtension.whyUse.description')}
            </p>
            <ul className="extension-list">
              <li>
                <strong>{t('extensionModal.browserExtension.whyUse.antiCrawlLabel')}</strong> {t('extensionModal.browserExtension.whyUse.antiCrawlText')}
              </li>
              <li>
                <strong>{t('extensionModal.browserExtension.whyUse.paywallLabel')}</strong> {t('extensionModal.browserExtension.whyUse.paywallText')}
              </li>
            </ul>
          </div>

          <div className="extension-subsection">
            <h4>{t('extensionModal.browserExtension.howToUse.title')}</h4>
            <ol className="extension-steps">
              <li>{t('extensionModal.browserExtension.howToUse.step1')}</li>
              <li>{t('extensionModal.browserExtension.howToUse.step2')}</li>
              <li>{t('extensionModal.browserExtension.howToUse.step3')}</li>
              <li>{t('extensionModal.browserExtension.howToUse.step4')}</li>
            </ol>
          </div>

          <p className="extension-tip">
            {t('extensionModal.browserExtension.pinTip')}
          </p>
        </section>

        <div className="extension-divider" />

        {/* MCP Setup Section */}
        <section className="extension-section mcp-setup-section">
          <div className="email-save-header">
            <Code size={ICON_SIZE.lg} />
            <h3>{t('extensionModal.mcp.title')}</h3>
          </div>
          <p>
            {t('extensionModal.mcp.description')}
          </p>

          {/* API Token */}
          <div className="mcp-item">
            <label className="mcp-label">
              <Key size={ICON_SIZE.sm} />
              {t('extensionModal.mcp.apiToken')}
            </label>
            <p className="mcp-hint">
              {t('extensionModal.mcp.apiTokenHint')}
            </p>

            {tokenError && (
              <div className="mcp-error">
                <AlertTriangle size={ICON_SIZE.xs} />
                {tokenError}
              </div>
            )}

            {!session?.access_token ? (
              <p className="mcp-hint">{t('extensionModal.mcp.loginToGenerate')}</p>
            ) : tokenInfo?.has_token && !showNewToken ? (
              // User has a token but we're not showing the new one
              <div className="mcp-token-existing">
                <div className="mcp-token-info">
                  <span className="mcp-token-prefix">rdk_••••••••</span>
                  <span className="mcp-token-meta">
                    {t('extensionModal.mcp.created')}: {tokenInfo.created_at ? new Date(tokenInfo.created_at).toLocaleDateString() : t('extensionModal.mcp.unknown')}
                    {tokenInfo.last_used_at && (
                      <> · {t('extensionModal.mcp.lastUsed')}: {new Date(tokenInfo.last_used_at).toLocaleDateString()}</>
                    )}
                  </span>
                </div>
                <div className="mcp-token-actions">
                  <button
                    className="mcp-btn mcp-btn-secondary"
                    onClick={handleGenerateToken}
                    disabled={tokenLoading}
                  >
                    <RefreshCw size={ICON_SIZE.xs} className={tokenLoading ? 'spinning' : ''} />
                    {t('extensionModal.mcp.regenerate')}
                  </button>
                  <button
                    className="mcp-btn mcp-btn-danger"
                    onClick={handleRevokeToken}
                    disabled={tokenLoading}
                  >
                    {t('extensionModal.mcp.revoke')}
                  </button>
                </div>
              </div>
            ) : showNewToken && apiToken ? (
              // Show newly generated token
              <div className="mcp-token-new">
                <div className="mcp-token-warning">
                  <AlertTriangle size={ICON_SIZE.xs} />
                  <strong>{t('extensionModal.mcp.copyNowWarning')}</strong> {t('extensionModal.mcp.shownOnceHint')}
                </div>
                <div className="mcp-token-container">
                  <code className="mcp-token-display mcp-token-full">
                    {truncatedApiToken}
                  </code>
                  <button
                    className="mcp-copy-btn"
                    onClick={handleCopyToken}
                    title={t('extensionModal.mcp.copyTokenToClipboard')}
                  >
                    {tokenCopied ? <Check size={ICON_SIZE.sm} /> : <Copy size={ICON_SIZE.sm} />}
                    <span>{tokenCopied ? t('extensionModal.mcp.copied') : t('extensionModal.mcp.copyToken')}</span>
                  </button>
                </div>
                <button
                  className="mcp-btn mcp-btn-text"
                  onClick={() => setShowNewToken(false)}
                >
                  {t('extensionModal.mcp.copiedMyToken')}
                </button>
              </div>
            ) : (
              // No token exists
              <div className="mcp-token-generate">
                <button
                  className="mcp-btn mcp-btn-primary"
                  onClick={handleGenerateToken}
                  disabled={tokenLoading}
                >
                  {tokenLoading ? (
                    <RefreshCw size={ICON_SIZE.xs} className="spinning" />
                  ) : (
                    <Key size={ICON_SIZE.xs} />
                  )}
                  {t('extensionModal.mcp.generateToken')}
                </button>
              </div>
            )}
          </div>

          {/* MCP Configuration */}
          <div className="mcp-item">
            <label className="mcp-label">{t('extensionModal.mcp.configuration')}</label>
            <p className="mcp-hint">
              {t('extensionModal.mcp.configHint')}
              {!apiToken && ` ${t('extensionModal.mcp.generateFirstHint')}`}
            </p>
            <pre className="mcp-config-display">
{`{
  "mcpServers": {
    "reedle": {
      "type": "http",
      "url": "${MCP_SERVER_URL}",
      "headers": {
        "Authorization": "Bearer ${apiToken || '<YOUR_API_TOKEN>'}"
      }
    }
  }
}`}
            </pre>
            <button
              className="mcp-copy-btn mcp-copy-config-btn"
              onClick={handleCopyConfig}
              title={t('extensionModal.mcp.copyConfigWithToken')}
            >
              {configCopied ? <Check size={ICON_SIZE.sm} /> : <Copy size={ICON_SIZE.sm} />}
              <span>{configCopied ? t('extensionModal.mcp.copied') : t('extensionModal.mcp.copyConfig')}</span>
            </button>
          </div>

          {/* Available Tools */}
          <div className="mcp-item">
            <label className="mcp-label">{t('extensionModal.mcp.availableTools')}</label>
            <p className="mcp-hint">
              {t('extensionModal.mcp.availableToolsHint')}
            </p>
            <div className="mcp-tools-badges">
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.articles')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.lists')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.highlights')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.comments')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.flashcards')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.tags')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.search')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.processing')}</span>
              <span className="mcp-tool-badge">{t('extensionModal.mcp.tools.credits')}</span>
            </div>
            <a
              href={MCP_SERVER_URL.replace('/mcp', '')}
              target="_blank"
              rel="noopener noreferrer"
              className="mcp-server-link"
            >
              <ExternalLink size={ICON_SIZE.xs} />
              {t('extensionModal.mcp.viewServerInfo')}
            </a>
          </div>
      </section>
    </Dialog>
  );
};

export default ExtensionModal;
